import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useListFormCatalogQuery,
  useListFormNotifyRecipientsQuery,
  useAddFormNotifyRecipientMutation,
  useDeleteFormNotifyRecipientMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const inputStyle = {
  width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', background: 'white',
};

// One recipient list for a given form. Add/delete go through the parent's
// mutations; the parent owns the (shared) query and refetch.
function RecipientList({ title, formKey, recipients, onAdd, onDelete, adding }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const cleaned = email.trim();
    if (!cleaned) return;
    try {
      await onAdd({ form_key: formKey, email: cleaned });
      setEmail('');
    } catch (err) {
      setError(err?.data?.detail || 'Could not add recipient.');
    }
  };

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <h6 style={{ color: brand.deepBlue, fontWeight: 700, margin: 0 }}>
          <i className="bi bi-file-earmark-text me-2" />{title}
        </h6>
        <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>Emailed when this form is submitted.</span>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@prosperitycapitaladvisors.com"
          style={{ ...inputStyle, maxWidth: 360 }}
        />
        <button
          type="submit"
          disabled={adding || !email.trim()}
          style={{
            background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
            padding: '6px 14px', fontWeight: 600, fontSize: '0.85rem',
            opacity: (adding || !email.trim()) ? 0.7 : 1,
          }}
        >
          <i className="bi bi-plus-lg me-1" />Add
        </button>
        {error && (
          <span style={{ color: '#b00020', fontSize: '0.85rem' }}>
            <i className="bi bi-exclamation-triangle me-1" />{error}
          </span>
        )}
      </form>

      {recipients.length === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.85rem', padding: '0.25rem 0' }}>
          No recipients yet — add one above.
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {recipients.map((r) => (
            <span
              key={r.id}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 999,
                padding: '0.25rem 0.6rem', fontSize: '0.82rem', color: brand.deepBlue,
              }}
            >
              <i className="bi bi-envelope" /> {r.email}
              <button
                onClick={() => onDelete(r)}
                title="Remove"
                style={{ background: 'none', border: 'none', color: '#b00020', padding: 0, lineHeight: 1 }}
              >
                <i className="bi bi-x-circle-fill" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
RecipientList.propTypes = {
  title: PropTypes.string.isRequired,
  formKey: PropTypes.string.isRequired,
  recipients: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  adding: PropTypes.bool,
};

// HR-configured recipient lists for form-submission notifications — one list per
// form in the catalog. When a matching form is submitted, everyone here is
// emailed.
export default function FormNotificationsSection() {
  const { data: catalog } = useListFormCatalogQuery();
  const { data, isLoading, isError, refetch } = useListFormNotifyRecipientsQuery({});
  const [addRecipient, addState] = useAddFormNotifyRecipientMutation();
  const [deleteRecipient] = useDeleteFormNotifyRecipientMutation();

  const forms = catalog?.forms || [];
  const all = useMemo(() => data?.recipients || [], [data]);
  const byForm = useMemo(() => {
    const m = {};
    for (const r of all) (m[r.form_key] = m[r.form_key] || []).push(r);
    return m;
  }, [all]);

  // Tags should refetch, but don't rely on tag config — refetch after writes.
  const onAdd = async (body) => {
    await addRecipient(body).unwrap();
    refetch();
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Remove ${r.email} from this notification list?`)) return;
    try {
      await deleteRecipient({ id: r.id }).unwrap();
      refetch();
    } catch (err) {
      window.alert(err?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', marginBottom: '1.5rem' }}>
      <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem' }}>
        <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
          <i className="bi bi-bell me-2" />Form Submission Notifications
        </h5>
      </div>

      <div style={{ padding: '1.25rem' }}>
        {isLoading ? (
          <div style={{ padding: '1.5rem', textAlign: 'center' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : isError ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: brand.charcoal }}>
            Unable to load notification recipients.
          </div>
        ) : forms.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: brand.charcoal }}>
            No forms are configured yet.
          </div>
        ) : (
          forms.map((f, i) => (
            <React.Fragment key={f.key}>
              {i > 0 && <hr style={{ borderColor: brand.softGray }} />}
              <RecipientList
                title={f.label}
                formKey={f.key}
                recipients={byForm[f.key] || []}
                onAdd={onAdd}
                onDelete={onDelete}
                adding={addState.isLoading}
              />
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
}
